import { useMemo, useState } from 'react';
import { MapPin, Plus, X } from 'lucide-react';

import { Combobox } from '@/components/Combobox';
import {
  useAddresses, useSaveStorageLocation, useStorageLocations,
} from './useLocations';

interface StorageLocationPickerProps {
  value: number | null;
  onChange: (id: number | null) => void;
  label?: string;
  placeholder?: string;
}

/**
 * Selector reutilizable de ubicación física: muestra todas las storage_locations
 * existentes con su path completo ("Casa › Cava › Pila A"), con buscador inline.
 * Permite también crear una ubicación rápida dentro de un domicilio existente.
 */
export function StorageLocationPicker({
  value, onChange, label = 'Ubicación física',
  placeholder = 'Buscar ubicación…',
}: StorageLocationPickerProps) {
  const { data: locations } = useStorageLocations();
  const { data: addresses } = useAddresses();
  const save = useSaveStorageLocation();

  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newParentId, setNewParentId] = useState<number | null>(null);
  const [newAddressId, setNewAddressId] = useState<number | null>(null);

  const options = useMemo(
    () =>
      (locations ?? []).map((l) => ({
        id: l.id,
        label: l.display_path ?? l.label,
        hint: l.type_label,
      })),
    [locations],
  );

  const createNew = async () => {
    if (!newLabel.trim()) return;
    // Si hay parent elegido, address_id se hereda. Sino se requiere uno.
    const parent = locations?.find((l) => l.id === newParentId);
    const addressId = parent?.address_id ?? newAddressId;
    if (!addressId) {
      alert('Elegí un domicilio para la nueva ubicación.');
      return;
    }
    const saved = await save.mutateAsync({
      payload: {
        address_id: addressId,
        parent_id: newParentId,
        type: 'shelf',
        label: newLabel.trim(),
      },
    });
    onChange(saved.id);
    setCreating(false);
    setNewLabel('');
    setNewParentId(null);
    setNewAddressId(null);
  };

  return (
    <div className="space-y-2">
      <label className="label inline-flex items-center gap-2">
        <MapPin size={12} /> {label}
      </label>
      <Combobox
        value={value}
        placeholder={placeholder}
        options={options}
        onChange={(id) => onChange(id ? Number(id) : null)}
      />

      {!creating ? (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
        >
          <Plus size={11} /> Nueva ubicación
        </button>
      ) : (
        <div className="border border-notion-200 rounded p-2 space-y-2">
          <p className="text-xs font-medium text-notion-800">Crear nueva ubicación</p>
          <input
            className="input py-1 text-sm"
            placeholder="ej. Pila A, Caja 17"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              className="input py-1 text-xs"
              value={newParentId ?? ''}
              onChange={(e) => setNewParentId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Sin ubicación padre (raíz)</option>
              {locations?.map((l) => (
                <option key={l.id} value={l.id}>
                  Dentro de: {l.display_path}
                </option>
              ))}
            </select>
            {!newParentId && (
              <select
                className="input py-1 text-xs"
                value={newAddressId ?? ''}
                onChange={(e) =>
                  setNewAddressId(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">Elegir domicilio…</option>
                {addresses?.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex gap-1.5">
            <button
              type="button"
              className="btn-secondary text-xs flex-1"
              onClick={() => setCreating(false)}
            >
              <X size={11} /> Cancelar
            </button>
            <button
              type="button"
              className="btn-primary text-xs flex-1"
              disabled={!newLabel.trim() || save.isPending}
              onClick={createNew}
            >
              <Plus size={11} /> Crear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
